<template>
  <div class="menu-management-container">
    <div class="menu-header">
      <h1>Menu Management</h1>
      <button class="btn-add-menu" @click="openAddMenuModal" title="Add New Menu">
        <i class="fas fa-plus"></i> Add New Menu
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <p>Loading menus...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="fetchMenus" class="btn-retry">Retry</button>
    </div>

    <!-- Menus Table -->
    <div v-else class="table-container">
      <div class="datatable-header">
        <span class="v-input-icon-left">
          <i class="pi pi-search"></i>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search menu..." 
            class="search-field"
          />
        </span>
      </div>
      <v-data-table
        :headers="headers"
        :items="filteredMenus"
        :search="searchQuery"
        :items-per-page="itemsPerPage"
        :sort-by="[{ key: sortBy, order: sortDesc ? 'desc' : 'asc' }]"
        class="elevation-0 menus-datatable"
        item-key="menuid"
        hover
      >

        <template #[`item.icon`]="{ item }">
          <i v-if="item.menu_icon" :class="item.menu_icon" :title="item.menu_icon"></i>
          <span v-else class="no-icon">-</span>
        </template>

        <template #[`item.is_active`]="{ item }">
          <span :class="item.is_active ? 'status-active' : 'status-inactive'">
            {{ item.is_active ? 'Active' : 'Inactive' }}
          </span>
        </template>

        <template #[`item.actions`]="{ item }">
          <div class="actions-cell" v-if="hasPermissions">
            <button @click="editMenu(item)" class="btn-edit" title="Edit">
              <i class="fas fa-edit"></i>
            </button>
          </div>
        </template>

        <template #no-data>
          <div class="no-data">No menus found</div>
        </template>
      </v-data-table>
    </div>

    <!-- Add/Edit Menu Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ isEditMode ? 'Edit Menu' : 'Add New Menu' }}</h2>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>

        <form @submit.prevent="saveMenu" class="modal-form">
          <div class="form-group">
            <label for="menu-name">Menu Name *</label>
            <input 
              id="menu-name"
              v-model="formData.menu_name" 
              type="text" 
              placeholder="Enter menu name"
              required
              class="form-input"
            >
            <span v-if="errors.menu_name" class="error-text">{{ errors.menu_name }}</span>
          </div>

          <div class="form-group">
            <label for="menu-action">Menu Action *</label>
            <input 
              id="menu-action"
              v-model="formData.menu_action" 
              type="text" 
              placeholder="Enter menu action (e.g., dashboard, profile)"
              required
              class="form-input"
            >
            <span v-if="errors.menu_action" class="error-text">{{ errors.menu_action }}</span>
          </div>

          <div class="form-group">
            <label for="parent-menu">Parent Menu</label>
            <select 
              id="parent-menu"
              v-model="formData.parent_id" 
              class="form-input"
            >
              <option :value="0">Root (No Parent)</option>
              <option 
                v-for="menu in parentMenuOptions" 
                :key="menu.menuid" 
                :value="menu.menuid"
              >
                {{ menu.menu_name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="menu-icon">Menu Icon</label>
            <input 
              id="menu-icon"
              v-model="formData.menu_icon" 
              type="text" 
              placeholder="Enter icon class (e.g., fas fa-home)"
              class="form-input"
            >
            <small class="help-text">FontAwesome or MDI icon class</small>
          </div>

          <div class="form-group">
            <label for="order-id">Menu Order ID *</label>
            <input 
              id="order-id"
              v-model.number="formData.order_id" 
              type="number" 
              placeholder="Enter order position"
              required
              min="0"
              class="form-input"
            >
            <span v-if="errors.order_id" class="error-text">{{ errors.order_id }}</span>
          </div>

          <div class="form-group checkbox-group">
            <label>
              <input 
                v-model="formData.is_active" 
                type="checkbox"
              >
              Active
            </label>
          </div>

          <div class="modal-footer">
            <button type="button" @click="closeModal" class="btn-cancel">Cancel</button>
            <button type="submit" class="btn-save" :disabled="isSubmitting">
              <span v-if="isSubmitting">
                <i class="fas fa-spinner fa-spin"></i> Saving...
              </span>
              <span v-else>Save Menu</span>
            </button>
          </div>
        </form>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';

export default {
  name: 'MenuContent',
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      menus: [],
      parentMenus: [],
      filteredMenus: [],
      searchQuery: '',
      isLoading: false,
      isSubmitting: false,
      error: null,
      hasPermissions: true,
      showModal: false,
      isEditMode: false,
      menuToDelete: null,
      sortBy: 'menu_name',
      sortDesc: false,
      itemsPerPage: 10,
      formData: {
        menu_name: '',
        menu_action: '',
        parent_id: 0,
        menu_icon: '',
        order_id: 0,
        is_active: true,
      },
      errors: {},
      headers: [
        { title: 'Menu Name', key: 'menu_name', sortable: true },
        { title: 'Menu Action', key: 'menu_action', sortable: true },
        { title: 'Parent Menu', key: 'parent_menu', sortable: false },
        { title: 'Icon', key: 'icon', sortable: false },
        { title: 'Order ID', key: 'order_id', sortable: true },
        { title: 'Status', key: 'is_active', sortable: true },
        { title: 'Actions', key: 'actions', sortable: false },
      ],
    };
  },
  computed: {
    parentMenuOptions() {
      // Filter out the current menu being edited from parent options
      return this.parentMenus.filter(menu => 
        this.isEditMode ? menu.menuid !== this.formData.menuid : true
      );
    },
  },
  watch: {
    searchQuery() {
      this.filterMenus();
    },
  },
  mounted() {
    this.fetchMenus();
    this.fetchParentMenus();
  },
  methods: {
    async fetchMenus() {
      this.isLoading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/menulist', config);
        //console.log('Menus fetched:', response.data);

        // Handle different response formats
        let menuList = response.data[2];
        //console.log('Raw menu list:', menuList);
        this.menus = menuList;
        this.filterMenus();
      } catch (error) {
        console.error('Error fetching menus:', error);
        this.error = 'Failed to load menus. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchParentMenus() {
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };

        const response = await axios.get('/parentmenulist', config);
        //console.log('Parent menus fetched:', response.data);

        // Handle different response formats
        let parentMenuList = response.data[2] || response.data;
        //console.log('Raw parent menu list:', parentMenuList);
        this.parentMenus = Array.isArray(parentMenuList) ? parentMenuList : [];
      } catch (error) {
        console.error('Error fetching parent menus:', error);
        // Fallback: use main menus as parent options if endpoint fails
        this.parentMenus = this.menus;
      }
    },

    filterMenus() {
      if (!this.searchQuery) {
        this.filteredMenus = [...this.menus];
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredMenus = this.menus.filter(menu =>
        menu.menu_name.toLowerCase().includes(query) ||
        menu.menu_action.toLowerCase().includes(query) ||
        menu.parent_menu.toLowerCase().includes(query)
      );
    },

    getParentMenuName(parentId) {
      const parent = this.menus.find(m => m.menuid === parentId);
      return parent ? parent.menu_name : 'Unknown';
    },

    openAddMenuModal() {
      this.isEditMode = false;
      this.formData = {
        menu_name: '',
        menu_action: '',
        parent_id: 0,
        menu_icon: '',
        order_id: 0,
        is_active: true,
      };
      this.errors = {};
      this.showModal = true;
    },

    editMenu(menu) {
      this.isEditMode = true;
      this.formData = { ...menu };
      // Convert is_active to boolean for checkbox
      this.formData.is_active = menu.is_active === 1 || menu.is_active === true;
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.isEditMode = false;
      this.formData = {
        menu_name: '',
        menu_action: '',
        parent_id: 0,
        menu_icon: '',
        order_id: 0,
        is_active: true,
      };
      this.errors = {};
    },

    validateForm() {
      this.errors = {};
      
      if (!this.formData.menu_name.trim()) {
        this.errors.menu_name = 'Menu name is required';
      }
      
      if (!this.formData.menu_action.trim()) {
        this.errors.menu_action = 'Menu action is required';
      }
      
      if (this.formData.order_id === null || this.formData.order_id === '') {
        this.errors.order_id = 'Order ID is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    async saveMenu() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        };
        
        // Prepare data - convert boolean is_active to 1 or 0
        const menuData = {
          ...this.formData,
          is_active: this.formData.is_active ? 1 : 0,
        };
        
        const dataform = JSON.stringify(menuData);
        //console.log(dataform);
        const data = new FormData();
        data.append('transaction', dataform);
        
        if (this.isEditMode) {
          // Update existing menu
          await axios.put(`/update_menu/${menuData.menuid}`, data, config);
        } else {
          // Create new menu
          await axios.post('/create_menu', data, config);
        }

        //console.log('Menu saved successfully:', response.data);
        
        // Show success message
        this.toast.success(this.isEditMode ? 'Menu updated successfully!' : 'Menu created successfully!');

        // Refresh menu list
        await this.fetchMenus();
        this.closeModal();
      } catch (error) {
        console.error('Error saving menu:', error);
        this.error = error.response?.data?.message || 'Failed to save menu. Please try again.';
        
        // Show error message
        this.toast.error(this.error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.menu-management-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.menu-header h1 {
  margin: 0;
  font-size: 28px;
  color: #333;
}

.btn-add-menu {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.btn-add-menu:hover {
  background-color: #45a049;
}

.btn-add-menu:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.loading-state,
.error-state {
  background: white;
  padding: 40px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state {
  color: #d32f2f;
}

.btn-retry {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 15px;
}

.btn-retry:hover {
  background-color: #45a049;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  padding: 20px;
}

.datatable-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.v-input-icon-left {
  position: relative;
  display: inline-block;
  width: 100%;
}

.v-input-icon-left .pi {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
  z-index: 1;
}

.search-field {
  width: 250px;
  padding: 8px 10px 8px 32px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  position: relative;
  z-index: 0;
}

.search-field:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.menus-table {
  width: 100%;
  border-collapse: collapse;
}

.menus-table thead {
  background-color: #f9f9f9;
  border-bottom: 2px solid #ddd;
}

.menus-table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.menus-table tbody tr {
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.menus-table tbody tr:hover {
  background-color: #f5f5f5;
}

.menus-table td {
  padding: 15px;
  color: #666;
  font-size: 14px;
}

.menu-row:last-child {
  border-bottom: none;
}

.no-data {
  text-align: center;
  color: #999;
  padding: 20px;
  font-style: italic;
}

.no-icon {
  color: #bbb;
}

.status-active {
  color: #4CAF50;
  font-weight: 600;
}

.status-inactive {
  color: #f44336;
  font-weight: 600;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.btn-edit,
.btn-delete {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.btn-edit {
  color: #2196F3;
}

.btn-edit:hover {
  background-color: rgba(33, 150, 243, 0.1);
}

.btn-delete {
  color: #f44336;
}

.btn-delete:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.confirmation {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.modal-body p {
  margin: 0 0 10px 0;
  color: #666;
}

.modal-body .warning {
  color: #f44336;
  font-weight: 600;
}

.modal-form {
  padding: 15px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.form-group {
  margin-bottom: 0;
}

.form-group:nth-child(3) {
  grid-column: 1 / -1;
}

.checkbox-group {
  grid-column: 1 / -1;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #333;
  font-size: 13px;
}

.form-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
}

.form-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.error-text {
  color: #f44336;
  font-size: 11px;
  margin-top: 3px;
  display: block;
}

.help-text {
  color: #999;
  font-size: 11px;
  margin-top: 3px;
  display: block;
}

.checkbox-group label {
  display: flex;
  align-items: center;
  font-weight: normal;
  margin-bottom: 0;
}

.checkbox-group input[type="checkbox"] {
  margin-right: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  background-color: #f9f9f9;
}

.btn-cancel {
  background-color: #e0e0e0;
  color: #333;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.3s ease;
}

.btn-cancel:hover {
  background-color: #d0d0d0;
}

.btn-save {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s ease;
}

.btn-save:hover:not(:disabled) {
  background-color: #45a049;
}

.btn-save:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* Vuetify DataTable Customization */
:deep(.menus-datatable) {
  font-size: 14px;
}

:deep(.menus-datatable .v-table__wrapper) {
  border-radius: 4px;
}

:deep(.menus-datatable thead tr) {
  background-color: #f9f9f9 !important;
}

:deep(.menus-datatable thead tr th) {
  background-color: #f9f9f9 !important;
  border-color: #ddd !important;
  color: #333 !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #ddd !important;
}

:deep(.menus-datatable tbody tr) {
  transition: background-color 0.2s ease;
}

:deep(.menus-datatable tbody tr:hover) {
  background-color: #f5f5f5 !important;
}

:deep(.menus-datatable tbody tr td) {
  color: #666;
  border-color: #e0e0e0;
  padding: 12px 15px;
}

:deep(.v-pagination) {
  margin-top: 15px;
}

:deep(.v-pagination .v-pagination__item) {
  color: #666;
}

:deep(.v-pagination .v-pagination__item--is-active) {
  background-color: #4CAF50 !important;
  color: white !important;
}

:deep(.v-data-table__td) {
  vertical-align: middle;
}
</style>
